<template>
  <div class="drawer-root">
    <div class="drawer-scrim" @click="$emit('close')"></div>
    <aside class="drawer" role="dialog" aria-modal="true">
      <!-- Header — same chrome as the internal drawer, nothing editable. -->
      <header class="drawer-head">
        <div class="dh-titles">
          <span class="dh-eyebrow">{{ t('drawer.details') }} · #{{ task.id }}</span>
          <h2 class="dh-title">{{ task.title }}</h2>
          <div class="dh-meta">
            <span :class="['badge', 'badge-' + task.status]">{{ t('status.' + task.status) }}</span>
            <span :class="['badge', 'badge-' + task.urgency]">{{ t('urgency.' + task.urgency) }}</span>
            <span v-if="task.due_at" class="dh-due">
              <span class="dh-due-label">{{ t('taskForm.dueDate') }}</span>{{ fmtDue(task.due_at) }}
            </span>
          </div>
        </div>
        <button class="icon-btn" :title="t('drawer.close')" @click="$emit('close')">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </header>

      <div class="drawer-body">
        <!-- Description — rich text written by the team (may embed images and
             PDF links). Fetched from the single-task endpoint; sanitized. -->
        <section class="block flush">
          <h3 class="block-title">{{ t('taskForm.description') }}</h3>
          <div v-if="!descFetched" class="desc-loading"><span class="ss-dot"></span></div>
          <div v-else-if="descHtml" class="desc-view" v-html="descHtml"></div>
          <p v-else class="muted-sm">{{ t('portal.noDescription') }}</p>
        </section>

        <section class="block">
          <TaskSubtasks :task-id="task.id" read-only />
        </section>
        <section class="block">
          <TaskComments :task-id="task.id" :can-comment="task.status !== 'done'" />
        </section>
      </div>
    </aside>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import api from '@/api'
import TaskSubtasks from '@/components/TaskSubtasks.vue'
import TaskComments from '@/components/TaskComments.vue'
import { useI18n } from '@/composables/useI18n'

// Allowlist sanitizer for the internally-authored rich-text description:
// drops active/embedding elements and event/javascript: attributes, keeps
// formatting, uploaded images and PDF links.
const BLOCKED_TAGS = 'script,style,iframe,object,embed,form,link,meta,base'
function cleanHtml(html) {
  const root = document.createElement('div')
  root.innerHTML = html
  root.querySelectorAll(BLOCKED_TAGS).forEach(el => el.remove())
  root.querySelectorAll('*').forEach(el => {
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase()
      const val = attr.value.trim().toLowerCase()
      if (name.startsWith('on') || ((name === 'href' || name === 'src' || name === 'xlink:href') && val.startsWith('javascript:'))) {
        el.removeAttribute(attr.name)
      }
    }
  })
  return root.innerHTML
}

export default {
  name: 'ClientTaskDrawer',
  components: { TaskSubtasks, TaskComments },
  props: {
    task: { type: Object, required: true },
  },
  emits: ['close'],
  setup(props) {
    const { t, locale } = useI18n()
    const descHtml    = ref('')
    const descFetched = ref(false)

    function fmtDue(dt) {
      const loc = locale.value === 'he' ? 'he-IL' : 'en-GB'
      return new Date(String(dt).replace(' ', 'T')).toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' })
    }

    onMounted(async () => {
      // List rows don't carry the description — fetch the full task on open.
      try {
        const full = (await api.getTask(props.task.id)).data
        descHtml.value = full?.description ? cleanHtml(full.description) : ''
      } catch {} finally { descFetched.value = true }
    })

    return { t, descHtml, descFetched, fmtDue }
  },
}
</script>

<style scoped>
/* Drawer chrome — mirrors TaskDetailDrawer (those styles are scoped there). */
.drawer-root { position: fixed; inset: 0; z-index: 1100; }
.drawer-scrim { position: absolute; inset: 0; background: rgba(16,22,40,.42); }
.drawer {
  position: absolute; inset-block: 14px; inset-inline-end: 14px;
  width: min(720px, calc(100% - 28px));
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(16,22,40,.22);
  display: flex; flex-direction: column;
  animation: drawer-in .2s ease;
}
@keyframes drawer-in { from { transform: translateY(8px); opacity: .4; } to { transform: none; opacity: 1; } }

.drawer-head {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 18px 20px; border-bottom: 1px solid var(--border);
}
.dh-titles { flex: 1; min-width: 0; }
.dh-eyebrow { font-family: var(--font-mono); font-size: 11px; color: var(--faint); letter-spacing: .03em; }
.dh-title { font-size: var(--fs-h4); font-weight: 700; margin-top: 3px; line-height: 1.3; }
.dh-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-top: 9px; }
.dh-due { font-size: 12.5px; color: var(--text); }
.dh-due-label { font-size: 11.5px; font-weight: 600; color: var(--muted); margin-inline-end: 5px; }

.drawer-body { flex: 1; overflow-y: auto; padding: 18px 20px; }

.block { border-top: 1px solid var(--border); padding-top: 16px; margin-top: 18px; }
.block.flush { border-top: none; padding-top: 0; margin-top: 0; }
.block-title { font-size: 14px; font-weight: 700; margin-bottom: 10px; }
.muted-sm { font-size: 12.5px; color: var(--faint); }

/* Description loading pulse — same as the internal drawer. */
.desc-loading {
  display: flex; align-items: center; justify-content: center;
  min-height: 88px; border-radius: var(--radius-sm); background: var(--surface-2);
}
.desc-loading .ss-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--muted); animation: ss-pulse 1s ease-in-out infinite;
}
@keyframes ss-pulse { 0%,100% { opacity: .3; } 50% { opacity: 1; } }

/* Rendered Quill content — keep the common formats readable. */
.desc-view { font-size: 14px; line-height: 1.65; overflow-wrap: break-word; }
.desc-view :deep(p) { margin: 0 0 6px; }
.desc-view :deep(h1) { font-size: 20px; font-weight: 700; margin: 12px 0 6px; }
.desc-view :deep(h2) { font-size: 17px; font-weight: 700; margin: 10px 0 6px; }
.desc-view :deep(h3) { font-size: 15px; font-weight: 700; margin: 8px 0 5px; }
.desc-view :deep(ul), .desc-view :deep(ol) { padding-inline-start: 22px; margin: 0 0 6px; }
.desc-view :deep(a) { color: var(--accent-ink, var(--accent)); }
.desc-view :deep(img) { max-width: 100%; height: auto; border-radius: 8px; margin: 4px 0; }
.desc-view :deep(blockquote) {
  margin: 6px 0; padding: 6px 12px;
  background: var(--surface-2); border-radius: var(--radius-sm); color: var(--muted);
}
.desc-view :deep(pre) {
  margin: 6px 0; padding: 10px 12px; overflow-x: auto;
  background: var(--surface-2); border-radius: var(--radius-sm);
  font-family: var(--font-mono); font-size: 12.5px;
}
.desc-view :deep(.ql-align-center) { text-align: center; }
.desc-view :deep(.ql-align-right) { text-align: right; }
.desc-view :deep(.ql-align-left) { text-align: left; }
.desc-view :deep(.ql-align-justify) { text-align: justify; }

/* TaskSubtasks / TaskComments carry inline-panel chrome — flatten inside the drawer. */
.drawer :deep(.subtasks-panel),
.drawer :deep(.comments-block) {
  margin: 0; padding: 0; border: none; background: none; border-radius: 0;
}
.drawer :deep(.sub-table) { width: 100%; }
.drawer :deep(.col-title) { width: auto; }
</style>
